/**
 * Построение контуров стен с правильными стыками (митрами).
 *
 * Стена — это не линия, а прямоугольник по толщине. В узле, где сходятся
 * несколько стен, их боковые грани нужно продлить до пересечения — иначе
 * во внешних углах остаются выемки, а на непрямых углах стык «разъезжается».
 *
 * Алгоритм: в каждом узле сортируем примыкающие стены по углу и для каждой
 * соседней пары пересекаем обращённые друг к другу грани. Полученная точка —
 * общий угол двух стен.
 *
 * Митра хороша для угла из двух стен. Когда стен в узле три и больше, она даёт
 * сквозной стене скошенный торец, а между стенами остаётся незакрытый треугольник.
 * Поэтому Т-образные стыки и кресты строятся иначе: сквозная стена заканчивается
 * ровным торцом по линии узла, примыкающие упираются в её грань. Если сквозной
 * стены нет (Y-образный стык), незакрытый многоугольник узла отдаём самой толстой стене.
 */
import {
	add,
	angleOf,
	leftNormal,
	lineIntersection,
	mul,
	norm,
	normalizeAngle,
	sub,
	type Pt,
} from "@/shared/lib/geometry";
import type { WallGraph } from "@/shared/plan";

/**
 * Контур одной стены: обход начинается с левого угла у узла a
 * (левая сторона — относительно направления a → b), идёт по левой грани к узлу b,
 * обходит торец у b и возвращается по правой грани. У стены, закрывающей
 * многоточечный стык, торец состоит из нескольких точек — поэтому контур
 * не обязательно четырёхугольник.
 */
export type WallOutline = Pt[];

const key = (nodeId: string, wallId: string) => `${nodeId}|${wallId}`;

interface Branch {
	wallId: string;
	/** Направление ОТ узла наружу */
	dir: Pt;
	/** Половина толщины стены */
	half: number;
	angle: number;
}

/** Насколько далеко от узла разрешаем уходить острому стыку (в полутолщинах). */
const MITER_LIMIT = 6;

/** Стены считаем продолжением друг друга, если угол между ними отличается от 180° меньше этого, рад. */
const THROUGH_TOLERANCE = Math.PI / 180;

export const buildWallOutlines = (plan: WallGraph): Record<string, WallOutline> => {
	// Углы, посчитанные в каждом узле для каждой примыкающей стены
	const cornerLeft: Record<string, Pt> = {};
	const cornerRight: Record<string, Pt> = {};
	/**
	 * Дополнительные точки торца: обход от правого угла к левому через углы
	 * остальных стен стыка. Есть только у стены, закрывающей многоточечный узел.
	 */
	const endFill: Record<string, Pt[]> = {};

	// Собираем «ветки» — стены, входящие в каждый узел
	const branches = new Map<string, Branch[]>();
	for (const wall of Object.values(plan.walls)) {
		const a = plan.nodes[wall.a];
		const b = plan.nodes[wall.b];
		if (!a || !b) continue;
		const half = wall.thickness / 2;
		const ab = norm(sub(b, a));
		if (ab.x === 0 && ab.y === 0) continue;

		const push = (nodeId: string, dir: Pt) => {
			const list = branches.get(nodeId) ?? [];
			list.push({ wallId: wall.id, dir, half, angle: angleOf(dir) });
			branches.set(nodeId, list);
		};
		push(wall.a, ab);
		push(wall.b, mul(ab, -1));
	}

	for (const [nodeId, list] of branches) {
		const node = plan.nodes[nodeId];
		if (!node) continue;
		list.sort((p, q) => p.angle - q.angle);

		// Одинокий конец стены — ровный торец
		if (list.length === 1) {
			const only = list[0];
			const n = leftNormal(only.dir);
			cornerLeft[key(nodeId, only.wallId)] = add(node, mul(n, only.half));
			cornerRight[key(nodeId, only.wallId)] = add(node, mul(n, -only.half));
			continue;
		}

		/** corners[i] — общий угол стен list[i] и list[i + 1] */
		const corners: Pt[] = [];
		for (let i = 0; i < list.length; i++) {
			const current = list[i];
			const next = list[(i + 1) % list.length];

			// Грани, обращённые в сектор между двумя соседними стенами
			const currentEdge = add(node, mul(leftNormal(current.dir), current.half));
			const nextEdge = add(node, mul(leftNormal(next.dir), -next.half));

			let corner = lineIntersection(currentEdge, current.dir, nextEdge, next.dir);

			// Стены продолжают друг друга (развёрнуты на 180°) — пересечения нет
			if (!corner) corner = currentEdge;

			// Очень острый угол уводит стык в бесконечность — подрезаем
			const away = sub(corner, node);
			const limit = MITER_LIMIT * Math.max(current.half, next.half);
			const distance = Math.hypot(away.x, away.y);
			if (distance > limit) corner = add(node, mul(norm(away), limit));

			corners.push(corner);
			cornerLeft[key(nodeId, current.wallId)] = corner;
			cornerRight[key(nodeId, next.wallId)] = corner;
		}

		// Две стены делят один торец — многоугольника узла нет.
		if (list.length < 3) continue;
		const count = list.length;
		const hasOpposite = (i: number) =>
			list.some((other, j) => {
				if (j === i) return false;
				const turn = Math.abs(normalizeAngle(other.angle - list[i].angle) - Math.PI);
				return turn < THROUGH_TOLERANCE;
			});
		const through = list.map((_, i) => hasOpposite(i));

		// Т-образный стык или крест: есть сквозная стена, к которой примыкают остальные.
		// Строим как в жизни — сквозная заканчивается ровным торцом по линии узла,
		// а примыкающие упираются в её грань (их углы уже лежат на этой грани).
		// Митра к граням примыкающей дала бы сквозной стене скошенный торец, который
		// виден при выделении стены. Пары сквозных стен в кресте накладываются
		// в квадрате узла — это внутренний объём, снаружи его не видно.
		if (through.some(Boolean)) {
			list.forEach((branch, i) => {
				if (!through[i]) return;
				const n = leftNormal(branch.dir);
				cornerLeft[key(nodeId, branch.wallId)] = add(node, mul(n, branch.half));
				cornerRight[key(nodeId, branch.wallId)] = add(node, mul(n, -branch.half));
			});
			continue;
		}

		// Сквозной стены нет (Y-образный стык): углы соседних пар образуют многоугольник
		// вокруг узла, который никто не накрывает. Его закрывает самая толстая стена.
		let host = 0;
		for (let i = 1; i < count; i++) if (list[i].half > list[host].half) host = i;

		// Торец стены-хозяина идёт от её правого угла corners[host − 1] к левому
		// corners[host] длинным путём — через углы всех остальных стен по порядку.
		const fill: Pt[] = [];
		for (let step = 2; step < count; step++) fill.push(corners[(host - step + count * 2) % count]);
		endFill[key(nodeId, list[host].wallId)] = fill;
	}

	const outlines: Record<string, WallOutline> = {};
	for (const wall of Object.values(plan.walls)) {
		const a = plan.nodes[wall.a];
		const b = plan.nodes[wall.b];
		if (!a || !b) continue;

		// В узле b стена «смотрит» в обратную сторону, поэтому левое и правое меняются местами
		const la = cornerLeft[key(wall.a, wall.id)];
		const rb = cornerRight[key(wall.b, wall.id)];
		const lb = cornerLeft[key(wall.b, wall.id)];
		const ra = cornerRight[key(wall.a, wall.id)];
		if (!la || !rb || !lb || !ra) continue;

		// Торец в каждом узле обходится от правого угла ветки к левому — в этот
		// промежуток и встают дополнительные точки многоугольника узла
		const fillB = endFill[key(wall.b, wall.id)] ?? [];
		const fillA = endFill[key(wall.a, wall.id)] ?? [];
		outlines[wall.id] = [la, rb, ...fillB, lb, ra, ...fillA];
	}
	return outlines;
};

/**
 * Длины двух граней стены по её контуру (с митрами в углах): короткая — в чистоте, со
 * стороны комнаты, как её меряют рулеткой; длинная — снаружи. У стены 6000 в чистоте из
 * перегородок 120 мм ось — 6120, а грани — 6000 и 6240. У одиночной стены без примыканий
 * обе грани равны оси. null — стены нет.
 */
export const wallFaceLengths = (
	plan: WallGraph,
	wallId: string,
): { clear: number; outer: number } | null => {
	const wall = plan.walls[wallId];
	const a = wall ? plan.nodes[wall.a] : undefined;
	const b = wall ? plan.nodes[wall.b] : undefined;
	const outline = buildWallOutlines(plan)[wallId];
	if (!a || !b || !outline) return null;
	const length = Math.hypot(b.x - a.x, b.y - a.y);
	if (length < 1) return null;
	const dir = { x: (b.x - a.x) / length, y: (b.y - a.y) / length };
	// Каждая грань — вершины контура по свою сторону от оси; её длина — их разброс вдоль оси
	const extent = (side: 1 | -1) => {
		let min = Infinity;
		let max = -Infinity;
		for (const p of outline) {
			const along = (p.x - a.x) * dir.x + (p.y - a.y) * dir.y;
			const across = -(p.x - a.x) * dir.y + (p.y - a.y) * dir.x;
			if (across * side <= 1) continue;
			min = Math.min(min, along);
			max = Math.max(max, along);
		}
		return Number.isFinite(min) ? max - min : length;
	};
	const left = extent(1);
	const right = extent(-1);
	return { clear: Math.min(left, right), outer: Math.max(left, right) };
};
